import { useQuery } from "@tanstack/react-query";
import { Trip } from "@/types/trip";
import { parseISO, isAfter } from "date-fns";
import Hey from "@/lib/hey";

interface AdResponse {
  imageUrl: string;
  link: string;
  tag: string;
}

const getUpcomingTripId = (trips: Trip[]): string | null => {
  if (!trips?.length) return null;
  
  const now = new Date();
  const upcomingTrip = trips.find(trip => {
    if (!trip.elements?.[0]?.startDate) return false;
    return isAfter(parseISO(trip.elements[0].startDate || "1970"), now);
  });
  
  console.debug('🎯 TRIP ID:', upcomingTrip?.id);
  console.debug('================================================');
  console.debug('🕒 First flight date:', upcomingTrip?.elements?.[0]?.startDate);
  console.debug('🔮 Is future trip?', upcomingTrip ? true : false);
  console.debug('================================================');
  
  return upcomingTrip?.id || null;
};

export const useTimelineAd = (trips: Trip[]) => {
  const tripId = getUpcomingTripId(trips);
  
  return useQuery({
    queryKey: ['timeline-ad', tripId],
    queryFn: async (): Promise<AdResponse> => {
      const baseUrl = 'https://api-test.getfeasy.com/hey/api/v3/advertisement';
      const params = new URLSearchParams({
        encodedConsentString: 'IamConsent',
        width: '320',
        height: '480'
      });
      
      if (tripId) {
        params.append('recloc', tripId);
      }
      
      const url = `${baseUrl}?${params.toString()}`;
      console.debug('🎯 Fetching ad from URL:', url);

      // Get the token from Hey utils
      const heyInstance = Hey.HeyUtils.getInstance();
      if (!heyInstance.isUserAuthenticated()) {
        throw new Error('User not authenticated');
      }

      // Then make the actual request
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${heyInstance.techToken}`,
          'User-Agent': 'CMTv4',
          'Cache-Control':'no-cache'
        },
      });
      
      if (!response.ok) {
        console.error('❌ Ad fetch failed:', response.status, response.statusText);
        throw new Error('Failed to fetch ad');
      }
      
      const data = await response.json();
      console.debug('✅ Ad data received:', {
        hasImageUrl: !!data.imageUrl,
        hasLink: !!data.link,
        hasTag: !!data.tag
      });
      
      return data;
    },
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
